'use client'
import { useRef, useState } from 'react'
import { motion, AnimatePresence, useInView, useReducedMotion } from 'framer-motion'
import { useLanguage } from '@/lib/context/LanguageContext'
import SectionHeader from '@/components/ui/SectionHeader'
import { GRADIENT_TEXT } from '@/lib/styles'
import { EASE, INVIEW_MARGIN } from '@/lib/constants/animations'
import { sectionNum } from '@/lib/utils'

// Bar geometry — locale-independent numbers. The 19→55% gap (36 points) is
// split into three 12-point sub-segments, one per solution lever. Every
// number the user reads comes from a localized i18n string, never these.
const BUDGET = 19 // ad budget online
const MEDIA = 55 // media time online
const GAP = MEDIA - BUDGET // 36
const SEG = GAP / 3 // 12
const TABS = [0, 1, 2]

// Endpoint pin — a labelled tick that anchors a % value to its bar point.
function Pin({ value, at }: { value: string; at: number }) {
  return (
    <div
      className="absolute flex flex-col items-center"
      style={{ insetInlineStart: `${at}%`, width: 0, bottom: '100%' }}
      aria-hidden
    >
      <span
        className="font-display font-black text-sm sm:text-base leading-none"
        style={{ color: '#0335b7' }}
      >
        {value}
      </span>
      <span className="w-px h-2.5 mt-1.5" style={{ background: '#0335b7' }} />
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#0335b7' }} />
    </div>
  )
}

export default function Problem() {
  const { t, lang, isRTL } = useLanguage()
  const p = t.problem
  const reduce = useReducedMotion()

  // One in-view trigger drives the three gap-closing overlays — the section's
  // single entrance animation. Everything else renders statically.
  const barRef = useRef<HTMLDivElement>(null)
  const inView = useInView(barRef, { once: true, margin: INVIEW_MARGIN.secondary })

  // The three gap segments are an ARIA tablist driving one solution panel.
  const [active, setActive] = useState(0)
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

  // linear-gradient angles are physical, not logical — flip for RTL.
  const barFill = `linear-gradient(${isRTL ? 225 : 135}deg,#0335b7,#00b4d8)`
  const hatch = `repeating-linear-gradient(${isRTL ? -45 : 45}deg,rgba(3,53,183,0.14) 0 6px,rgba(3,53,183,0) 6px 12px)`

  const num = (i: number) => sectionNum(String(i + 1).padStart(2, '0'), lang)
  const tabId = (i: number) => `prob-tab-${i}`

  // APG tabs, automatic activation. Arrow direction flips in RTL.
  const onTabKeyDown = (e: React.KeyboardEvent) => {
    const fwd = isRTL ? -1 : 1
    let next = active
    if (e.key === 'ArrowRight') next = (active + fwd + 3) % 3
    else if (e.key === 'ArrowLeft') next = (active - fwd + 3) % 3
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = 2
    else return
    e.preventDefault()
    setActive(next)
    tabRefs.current[next]?.focus()
  }

  return (
    <section id="problem" className="py-16 md:py-24 lg:py-32 border-b border-ink/5 bg-[#f6f8fc]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeader num="01" label={p.section} />

        <div className="rounded-2xl border border-ink/8 bg-white shadow-sm p-6 sm:p-8 md:p-10 lg:p-12 flex flex-col gap-8 md:gap-10">
          {/* problem headline */}
          <div>
            <h2 className="font-display font-black text-2xl sm:text-3xl md:text-4xl text-ink leading-snug">
              {p.leftTitle}
            </h2>
          </div>

          {/* The merged bar. Headroom holds the 36 callout + the 19/55 pins;
              footroom holds the desktop tab numerals / mobile tab row. */}
          <div ref={barRef} className="relative pt-28 sm:pt-32 pb-0 sm:pb-16">
            {/* the 36 — oversized, centered over the gap span */}
            <div
              className="absolute top-0 flex flex-col items-center text-center"
              style={{ insetInlineStart: `${BUDGET}%`, width: `${GAP}%` }}
              aria-hidden
            >
              <span
                className="font-display font-black text-5xl sm:text-6xl leading-[1.15] pb-1"
                style={GRADIENT_TEXT}
              >
                {p.gapNum}
              </span>
              <span className="mt-1.5 text-xs font-semibold" style={{ color: '#475569' }}>
                {p.gapLabel}
              </span>
            </div>

            {/* bar band — track, endpoint pins and the tablist */}
            <div className="relative">
              {/* 19% / 55% endpoint pins, anchored just above the bar */}
              <Pin value={p.s2val} at={BUDGET} />
              <Pin value={p.s1val} at={MEDIA} />

              {/* decorative track — meaning is carried by leftBody + tabs */}
              <div
                className="relative h-12 sm:h-14 rounded-full bg-tint overflow-hidden"
                aria-hidden
              >
                {/* budget captured — static solid 0→19% */}
                <div
                  className="absolute inset-y-0"
                  style={{ insetInlineStart: 0, width: `${BUDGET}%`, background: barFill }}
                />
                {/* the gap — static hatch 19→55% */}
                <div
                  className="absolute inset-y-0"
                  style={{
                    insetInlineStart: `${BUDGET}%`,
                    width: `${GAP}%`,
                    backgroundColor: 'rgba(3,53,183,0.05)',
                    backgroundImage: hatch,
                  }}
                />
                {/* three solid overlays — close the gap lever by lever. One
                    useInView, staggered scaleX → one continuous "zip". */}
                {TABS.map((i) => (
                  <motion.div
                    key={`fill-${i}`}
                    className="absolute inset-y-0"
                    style={{
                      insetInlineStart: `${BUDGET + SEG * i}%`,
                      width: `${SEG}%`,
                      background: barFill,
                      transformOrigin: isRTL ? 'right' : 'left',
                    }}
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: inView ? 1 : 0 }}
                    transition={{
                      duration: reduce ? 0 : 0.55,
                      ease: EASE,
                      delay: reduce ? 0 : 0.15 + i * 0.45,
                    }}
                  />
                ))}
                {/* inactive-segment dim layer — user-driven state, separate
                    from the entrance fill so it reads correctly mid-fill */}
                {TABS.map((i) => (
                  <div
                    key={`dim-${i}`}
                    className="absolute inset-y-0"
                    style={{
                      insetInlineStart: `${BUDGET + SEG * i}%`,
                      width: `${SEG}%`,
                      background: 'rgba(246,248,252,0.62)',
                      opacity: active === i ? 0 : 1,
                      transition: reduce ? 'none' : 'opacity 0.2s ease',
                    }}
                  />
                ))}
                {/* hairline dividers between sub-segments */}
                {[1, 2].map((i) => (
                  <span
                    key={`div-${i}`}
                    className="absolute inset-y-0 w-px bg-white/60"
                    style={{ insetInlineStart: `${BUDGET + SEG * i}%` }}
                  />
                ))}
              </div>

              {/* tablist — mobile: a full-size row below the bar; desktop:
                  transparent hit-zones overlaid on each gap segment. */}
              <div
                role="tablist"
                aria-label={p.tablistLabel}
                aria-orientation="horizontal"
                className="mt-6 flex gap-2 sm:mt-0 sm:absolute sm:inset-0 sm:gap-0"
              >
                {TABS.map((i) => {
                  const on = active === i
                  return (
                    <button
                      key={`tab-${i}`}
                      ref={(el) => {
                        tabRefs.current[i] = el
                      }}
                      type="button"
                      role="tab"
                      id={tabId(i)}
                      aria-selected={on}
                      aria-controls="prob-panel"
                      aria-label={p.tabLabels[i]}
                      tabIndex={on ? 0 : -1}
                      onMouseEnter={() => setActive(i)}
                      onFocus={() => setActive(i)}
                      onClick={() => setActive(i)}
                      onKeyDown={onTabKeyDown}
                      style={
                        {
                          '--s': `${BUDGET + SEG * i}%`,
                          '--w': `${SEG}%`,
                        } as React.CSSProperties
                      }
                      className={
                        'group relative cursor-pointer outline-none transition-colors ' +
                        'focus-visible:ring-2 focus-visible:ring-[#0335b7] focus-visible:ring-offset-2 focus-visible:ring-offset-white ' +
                        'flex-1 min-h-11 flex flex-col items-center justify-center gap-1 px-1.5 py-2 rounded-xl border ' +
                        (on ? 'border-[#0335b7] bg-[#0335b7]/[0.05] ' : 'border-ink/10 bg-white ') +
                        'sm:absolute sm:top-0 sm:h-28 sm:min-h-0 sm:gap-0 sm:p-0 sm:rounded-none sm:border-0 sm:bg-transparent ' +
                        'sm:[inset-inline-start:var(--s)] sm:[width:var(--w)]'
                      }
                    >
                      {/* mobile: numeral + short label */}
                      <span
                        className="sm:hidden font-display font-black text-base leading-none"
                        style={GRADIENT_TEXT}
                        aria-hidden
                      >
                        {num(i)}
                      </span>
                      <span
                        className="sm:hidden text-[11px] font-semibold leading-tight text-center"
                        style={{ color: on ? '#0335b7' : '#475569' }}
                      >
                        {p.tabLabels[i]}
                      </span>
                      {/* desktop: tick + numeral in the badge zone below the bar */}
                      <span
                        className="hidden sm:flex sm:flex-col sm:items-center sm:absolute sm:inset-x-0 transition-opacity duration-200"
                        style={{ top: '3.5rem', opacity: on ? 1 : 0.4 }}
                        aria-hidden
                      >
                        <span className="w-px h-5" style={{ background: 'rgba(3,53,183,0.35)' }} />
                        <span
                          className="mt-1.5 font-display font-black text-lg leading-none"
                          style={GRADIENT_TEXT}
                        >
                          {num(i)}
                        </span>
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          {/* problem ↔ solution, paired side by side */}
          <div className="grid md:grid-cols-2 gap-8 md:gap-10 lg:gap-12 pt-8 border-t border-ink/8">
            {/* the problem, in words — carries 55/19/36 regardless of interaction */}
            <p className="text-sm md:text-base leading-loose" style={{ color: '#475569' }}>
              {p.leftBody}
            </p>

            {/* the solution — one panel, driven by the active tab */}
            <div className="md:border-s md:border-ink/8 md:ps-8 lg:ps-12">
              <p
                className="text-[11px] font-bold uppercase tracking-[0.22em] mb-3"
                style={{ color: '#0335b7' }}
              >
                {p.solutionEyebrow}
              </p>
              <p className="text-sm md:text-base leading-loose" style={{ color: '#475569' }}>
                {p.rightBody}
              </p>

              <div
                role="tabpanel"
                id="prob-panel"
                aria-labelledby={tabId(active)}
                tabIndex={0}
                className="mt-6 pt-6 border-t border-ink/8 min-h-[5rem] outline-none"
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={active}
                    className="flex items-baseline gap-4 sm:gap-5"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: reduce ? 0 : 0.28, ease: EASE }}
                  >
                    <span
                      className="font-display font-black text-2xl sm:text-3xl leading-none shrink-0"
                      style={GRADIENT_TEXT}
                      aria-hidden
                    >
                      {num(active)}
                    </span>
                    <span
                      className="text-sm sm:text-base leading-relaxed"
                      style={{ color: 'rgba(11,22,41,0.8)' }}
                    >
                      {p.bullets[active]}
                    </span>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
